/**
 * V1 (Freqtrade方法模式) → V2 (逻辑流模式) 迁移工具
 */

import type {IndicatorNodeConfig, LogicFlowEdge, LogicFlowNode, StrategyVisualModelV2,} from '../types/logicFlow';
import type {StrategyVisualModel} from '../services/myStrategyService';

// ========== 正则模式 ==========
const INDICATOR_ASSIGN_PATTERN = /dataframe\[['"]([^'"]+)['"]\]\s*=\s*([^\n#]+)/g;
const LOC_CONDITION_PATTERN = /dataframe\.loc\[\s*([^,]+),\s*['"](enter_long|enter_short|exit_long|exit_short)['"]\s*\]\s*=\s*(\d+)/g;
const TA_FUNC_PATTERN = /ta\.(\w+)/;
const QTPY_FUNC_PATTERN = /qtpylib\.(\w+)/;

// ========== TA 参数名映射 ==========
const TA_PARAM_MAP: Record<string, Array<{ name: string; type: 'number' | 'string' | 'column' }>> = {
    RSI: [
        {name: 'dataframe_column', type: 'column'},
        {name: 'timeperiod', type: 'number'},
    ],
    EMA: [
        {name: 'dataframe_column', type: 'column'},
        {name: 'timeperiod', type: 'number'},
    ],
    SMA: [
        {name: 'dataframe_column', type: 'column'},
        {name: 'timeperiod', type: 'number'},
    ],
    MACD: [
        {name: 'fastperiod', type: 'number'},
        {name: 'slowperiod', type: 'number'},
        {name: 'signalperiod', type: 'number'},
    ],
    BBANDS: [
        {name: 'timeperiod', type: 'number'},
        {name: 'nbdevup', type: 'number'},
        {name: 'nbdevdn', type: 'number'},
    ],
    ATR: [
        {name: 'timeperiod', type: 'number'},
    ],
    ADX: [
        {name: 'timeperiod', type: 'number'},
    ],
    STOCH: [
        {name: 'fastk_period', type: 'number'},
        {name: 'slowk_period', type: 'number'},
        {name: 'slowd_period', type: 'number'},
    ],
    CCI: [
        {name: 'timeperiod', type: 'number'},
    ],
};

// ========== 主迁移入口 ==========
export interface MigrationResult {
    model: StrategyVisualModelV2;
    warnings: string[];
}

export function migrateV1ToV2(v1Model: StrategyVisualModel): MigrationResult {
    const warnings: string[] = [];
    const nodes: LogicFlowNode[] = [];
    const edges: LogicFlowEdge[] = [];

    // 1. 解析 indicators 方法体 → INDICATOR 节点
    const indicatorsBody = v1Model.methods?.populateIndicators?.body || '';
    if (indicatorsBody.trim() && 'return dataframe' !== indicatorsBody.trim()) {
        const indicatorResult = parseIndicators(indicatorsBody);
        for (const indicatorNode of indicatorResult.nodes) {
            nodes.push(indicatorNode);
        }
        warnings.push(...indicatorResult.warnings);
    }

    // 2. 解析 entry 方法体 → RULE 节点（合并 CONDITION + ACTION）
    const entryBody = v1Model.methods?.entry?.body || '';
    if (entryBody.trim()) {
        const entryResult = parseActionMethodAsRules(entryBody, 'entry');
        for (const node of entryResult.nodes) {
            nodes.push(node);
        }
        warnings.push(...entryResult.warnings);
    }

    // 3. 解析 exit 方法体 → RULE 节点（合并 CONDITION + ACTION）
    const exitBody = v1Model.methods?.exit?.body || '';
    if (exitBody.trim()) {
        const exitResult = parseActionMethodAsRules(exitBody, 'exit');
        for (const node of exitResult.nodes) {
            nodes.push(node);
        }
        warnings.push(...exitResult.warnings);
    }

    // 5. 构建 V2 模型
    const title = v1Model.title || v1Model.strategyName || '';
    const description = v1Model.description || '';
    const strategyName = v1Model.strategyName || v1Model.strategyClassName || 'MigratedStrategy';

    const timeframe = v1Model.properties?.timeframe || '5m';
    const canShort = v1Model.properties?.canShort || false;
    const stoploss = v1Model.properties?.stoploss || '-0.10';

    let minimalRoi: Record<string, number>;
    if (v1Model.properties?.minimalRoi && typeof v1Model.properties.minimalRoi === 'object') {
        minimalRoi = v1Model.properties.minimalRoi as Record<string, number>;
    } else {
        minimalRoi = {'0': 0.10};
    }

    warnings.push('已从旧版 Freqtrade 方法模式迁移为逻辑流模式，请检查节点和连线是否正确');

    return {
        model: {
            schemaVersion: '2.0',
            editorType: 'logic-flow',
            title,
            description,
            strategyName,
            strategyClassName: strategyName,
            config: {timeframe, canShort, stoploss, minimalRoi},
            graph: {nodes, edges},
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        },
        warnings,
    };
}

// ========== 检测版本 ==========
export function detectSchemaVersion(model: unknown): '1.0' | '2.0' | 'unknown' {
    if (!model || typeof model !== 'object') {
        return 'unknown';
    }
    const m = model as Record<string, unknown>;
    if ('2.0' === m.schemaVersion) {
        return '2.0';
    }
    if ('1.0' === m.schemaVersion || 'freqtrade-visual' === m.editorType) {
        return '1.0';
    }
    return 'unknown';
}

// ========== 辅助函数 ==========
let edgeCounter = 0;

function genEdgeId(): string {
    return `e_mig_${++edgeCounter}`;
}

interface ParseIndicatorsResult {
    nodes: LogicFlowNode[];
    warnings: string[];
}

function parseIndicators(body: string): ParseIndicatorsResult {
    const nodes: LogicFlowNode[] = [];
    const warnings: string[] = [];

    const matches = body.matchAll(INDICATOR_ASSIGN_PATTERN);
    for (const match of matches) {
        const indicatorName = match[1];
        const expression = match[2].trim();

        const taMatch = expression.match(TA_FUNC_PATTERN);
        const qtpylibMatch = expression.match(QTPY_FUNC_PATTERN);

        let func = expression;
        let params: Array<{ name: string; value: string; type: 'number' | 'string' | 'column' }> = [];

        if (taMatch) {
            func = `ta.${taMatch[1]}`;
            params = extractTaParams(taMatch[1], expression);
        } else if (qtpylibMatch) {
            func = `qtpylib.${qtpylibMatch[1]}`;
        }

        const node: LogicFlowNode = {
            id: `ind_${indicatorName}`,
            type: 'INDICATOR',
            label: indicatorName,
            config: {
                type: 'INDICATOR',
                indicatorName,
                function: func,
                params,
            } as IndicatorNodeConfig,
        };
        nodes.push(node);
    }

    if (0 === nodes.length) {
        warnings.push('未在 indicators 方法中识别到指标赋值语句');
    }

    return {nodes, warnings};
}

function extractTaParams(funcName: string, expression: string): Array<{
    name: string;
    value: string;
    type: 'number' | 'string' | 'column'
}> {
    const paramDefs = TA_PARAM_MAP[funcName];
    if (!paramDefs) {
        return [];
    }

    const argsStr = expression.substring(expression.indexOf('(') + 1, expression.lastIndexOf(')'));
    const args = argsStr.split(',').map(s => s.trim()).filter(s => s.length > 0);

    return paramDefs.map((def, index) => ({
        name: def.name,
        value: index < args.length ? args[index] : '',
        type: def.type,
    }));
}

interface ParseRuleResult {
    nodes: LogicFlowNode[];
    warnings: string[];
}

function parseActionMethodAsRules(body: string, label: string): ParseRuleResult {
    const nodes: LogicFlowNode[] = [];
    const warnings: string[] = [];

    const matches = body.matchAll(LOC_CONDITION_PATTERN);
    let matchCount = 0;

    for (const match of matches) {
        matchCount++;
        const conditionExpr = match[1].trim();
        const action = match[2] as 'enter_long' | 'enter_short' | 'exit_long' | 'exit_short';
        const signalValue = match[3];

        const rules = parseConditionExpression(conditionExpr);
        const ruleId = `rule_${label}_${matchCount}`;

        const ruleNode: LogicFlowNode = {
            id: ruleId,
            type: 'RULE',
            label: `${label} 规则 ${matchCount}`,
            config: {
                type: 'RULE',
                logicOperator: 'AND',
                rules,
                action,
                signalValue,
            },
        };

        nodes.push(ruleNode);
    }

    if (0 === matchCount) {
        warnings.push(`${label} 方法中未识别到条件赋值语句，请手动添加`);
    }

    return {nodes, warnings};
}

function parseConditionExpression(expr: string): Array<{
    leftOperand: string;
    operator: '>' | '<' | '>=' | '<=' | '==' | '!=' | 'cross_above' | 'cross_below';
    rightOperand: string
}> {
    const rules: Array<{
        leftOperand: string;
        operator: '>' | '<' | '>=' | '<=' | '==' | '!=' | 'cross_above' | 'cross_below';
        rightOperand: string
    }> = [];

    // 去掉外层括号
    let cleaned = expr.trim();
    if (cleaned.startsWith('(') && cleaned.endsWith(')')) {
        cleaned = cleaned.slice(1, -1).trim();
    }

    // 按 & 和 | 分割
    const parts = cleaned.split(/[&|]/).map(s => s.trim()).filter(s => s.length > 0);

    for (const part of parts) {
        const cleanedPart = part.replace(/^\(|\)$/g, '').trim();

        // 匹配比较符（从长到短）
        const comparisonMatch = cleanedPart.match(/^(.+?)\s*(>=|<=|==|!=|>|<)\s*(.+)$/);
        if (comparisonMatch) {
            rules.push({
                leftOperand: comparisonMatch[1].trim(),
                operator: comparisonMatch[2] as '>' | '<' | '>=' | '<=' | '==' | '!=',
                rightOperand: comparisonMatch[3].trim(),
            });
        }
    }

    return rules;
}
